import { NEW_TNA_SUMMARY_DETAILS } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import Header from "components/Common/Header";
import { Affix, Button, Card, Space, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { cglFormatDate } from "utils/functions";
import { getTnaSummaryStatus } from "./tnaSummaryUtils";
import "../Summary/summaryView.css";

const getStatusStyle = (status) => ({
  backgroundColor:
    status === "Regular"
      ? "#93cddd"
      : status === "Overdue"
        ? "#f66d6d"
        : "#ffc000",
  color:
    status === "Regular"
      ? "#045b73"
      : status === "Overdue"
        ? "#0f0f0f"
        : "#9f7803",
  border: "2px solid white",
  padding: "4px",
  textAlign: "center",
  wordWrap: "break-word",
  borderRadius: "4px",
  fontWeight: "500",
  display: "block",
});

function mapApiToPlanTasks(styleRecords) {
  const tasks = [];

  (styleRecords || []).forEach((styleRow) => {
    (styleRow.tna_style_tasks || []).forEach((taskRow) => {
      const relationWith = taskRow.task?.relation_with || "Style";
      const styleNos = styleRow.styles?.length
        ? styleRow.styles.map((style) => style.style_no)
        : [styleRow.style?.style_no].filter(Boolean);

      tasks.push({
        id: taskRow.id,
        title: taskRow.task?.title || "N/A",
        process: taskRow.task?.process?.title || "N/A",
        department: "N/A",
        relation_with: relationWith,
        responsible_person_name: taskRow.responsible_person?.name || "N/A",
        task_dependencies: [],
        task_dates: [
          {
            styles:
              relationWith === "Style"
                ? styleNos.map((style_no) => ({ style_no }))
                : [],
            purchase_order_maps: [],
            due_date: taskRow.due_date,
            action_status_date: taskRow.action_status_date,
            variance:
              taskRow.variance !== undefined && taskRow.variance !== null
                ? taskRow.variance
                : "N/A",
            action_status_string: taskRow.action_status || "N/A",
            status: getTnaSummaryStatus(taskRow.due_date),
          },
        ],
      });
    });
  });

  return tasks;
}

const renderTaskRows = (tasks) => {
  if (!tasks || tasks.length === 0) return null;

  const rows = [];

  tasks.forEach((task, index) => {
    const taskDate =
      task.task_dates && task.task_dates.length > 0
        ? task.task_dates[0]
        : null;

    if (!taskDate) return;

    let stylesCount = 0;
    let poCount = 0;

    if (
      task.relation_with === "Style" &&
      taskDate.styles &&
      taskDate.styles.length > 0
    ) {
      stylesCount = taskDate.styles.length;
    }

    if (
      task.relation_with === "PO" &&
      taskDate.purchase_order_maps &&
      taskDate.purchase_order_maps.length > 0
    ) {
      poCount = taskDate.purchase_order_maps.length;
    }

    const rowspan = Math.max(1, stylesCount, poCount);

    if (rowspan === 1) {
      rows.push(
        <tr key={`task-${task.id}`}>
          <td>{index + 1}</td>
          <td>{task.title}</td>
          <td>{task.process}</td>
          <td>{task.department}</td>
          <td>
            {task.relation_with === "Style" && taskDate.styles
              ? taskDate.styles.map((style) => style.style_no).join(", ")
              : "N/A"}
          </td>
          <td>
            {task.relation_with === "PO" && taskDate.purchase_order_maps
              ? taskDate.purchase_order_maps
                  .map((po) => po.original_po)
                  .join(", ")
              : "N/A"}
          </td>
          <td>{cglFormatDate(taskDate.due_date)}</td>
          <td>{cglFormatDate(taskDate.action_status_date) || "N/A"}</td>
          <td>{taskDate.variance}</td>
          <td>{taskDate.action_status_string || "N/A"}</td>
          <td>
            <div style={getStatusStyle(taskDate.status)}>
              {taskDate.status || "N/A"}
            </div>
          </td>
          <td>{task.responsible_person_name || "N/A"}</td>
          <td>
            {task.task_dependencies && task.task_dependencies.length > 0
              ? task.task_dependencies.map((dep) => dep.title).join(", ")
              : "N/A"}
          </td>
        </tr>,
      );
    } else {
      rows.push(
        <tr key={`task-${task.id}-row-0`}>
          <td rowSpan={rowspan}>{index + 1}</td>
          <td rowSpan={rowspan}>{task.title}</td>
          <td rowSpan={rowspan}>{task.process}</td>
          <td rowSpan={rowspan}>{task.department}</td>
          <td>
            {task.relation_with === "Style" &&
            taskDate.styles &&
            taskDate.styles.length > 0
              ? taskDate.styles[0].style_no
              : "N/A"}
          </td>
          <td>
            {task.relation_with === "PO" &&
            taskDate.purchase_order_maps &&
            taskDate.purchase_order_maps.length > 0
              ? taskDate.purchase_order_maps[0].original_po
              : "N/A"}
          </td>
          <td rowSpan={rowspan}>{cglFormatDate(taskDate.due_date)}</td>
          <td rowSpan={rowspan}>
            {cglFormatDate(taskDate.action_status_date) || "N/A"}
          </td>
          <td rowSpan={rowspan}>{taskDate.variance}</td>
          <td rowSpan={rowspan}>{taskDate.action_status_string || "N/A"}</td>
          <td rowSpan={rowspan}>
            <div style={getStatusStyle(taskDate.status)}>
              {taskDate.status || "N/A"}
            </div>
          </td>
          <td rowSpan={rowspan}>{task.responsible_person_name || "N/A"}</td>
          <td rowSpan={rowspan}>
            {task.task_dependencies && task.task_dependencies.length > 0
              ? task.task_dependencies.map((dep) => dep.title).join(", ")
              : "N/A"}
          </td>
        </tr>,
      );

      if (task.relation_with === "Style" && taskDate.styles) {
        for (let i = 1; i < taskDate.styles.length; i++) {
          rows.push(
            <tr key={`task-${task.id}-style-${i}`}>
              <td>{taskDate.styles[i].style_no}</td>
              <td>N/A</td>
            </tr>,
          );
        }
      } else if (
        task.relation_with === "PO" &&
        taskDate.purchase_order_maps
      ) {
        for (let i = 1; i < taskDate.purchase_order_maps.length; i++) {
          rows.push(
            <tr key={`task-${task.id}-po-${i}`}>
              <td>N/A</td>
              <td>{taskDate.purchase_order_maps[i].original_po}</td>
            </tr>,
          );
        }
      }
    }
  });

  return rows;
};

const NewSummaryView = () => {
  const { summaryId } = useParams();
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAllPermitList = useCallback(async () => {
    if (!summaryId) return;
    setLoading(true);
    const response = await getData(
      `${NEW_TNA_SUMMARY_DETAILS}/${summaryId}`,
      false,
      { per_page: 500 },
    );

    if (response && response?.data?.code === 200) {
      const paginated = response?.data?.data;
      const items = Array.isArray(paginated?.data)
        ? paginated.data
        : Array.isArray(paginated)
          ? paginated
          : [];
      setTaskList(mapApiToPlanTasks(items));
    } else {
      HandleNotification("error", "bottomRight", "Something Went Wrong!", null);
    }

    setLoading(false);
  }, [summaryId]);

  useEffect(() => {
    getAllPermitList();
  }, [getAllPermitList]);

  const extra = (
    <Space>
      <Button type="primary" size="small" style={{ float: "right" }}>
        <Link to="/tanda/new-summary">Back to List</Link>
      </Button>
    </Space>
  );

  return (
    <div className="mt-4" style={{ width: "100%" }}>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{
            width: "100%",
            boxShadow: "none",
          }}
        >
          <Header
            title="T&A Summary Details"
            subTitle=""
            actions={extra}
          />
        </Card>
      </Affix>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : (
        <div
          className="todo-table"
          style={{
            padding: "5px 0",
            maxHeight: "calc(100vh)",
            overflowY: "auto",
            backgroundColor: "white",
            width: "100%",
            overflowX: "auto",
            display: "block",
          }}
        >
          <table
            style={{
              width: "100%",
              tableLayout: "fixed",
              borderCollapse: "collapse",
              minWidth: "100%",
            }}
          >
            <colgroup>
              <col style={{ width: "3%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "6%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "8%" }} />
            </colgroup>
            <thead>
              <tr style={{ position: "sticky", top: 0, zIndex: 2 }}>
                <th className="t-header bc-java">Sl</th>
                <th className="t-header bc-java">Task</th>
                <th className="t-header bc-java">Process</th>
                <th className="t-header bc-java">Department</th>
                <th className="t-header bc-java">Style(s)</th>
                <th className="t-header bc-java">PO(s)</th>
                <th className="t-header bc-java">Due Date</th>
                <th className="t-header bc-java">Actual Date</th>
                <th className="t-header bc-java">Variance (Days)</th>
                <th className="t-header bc-java">Action</th>
                <th className="t-header bc-java">Status</th>
                <th className="t-header bc-java">Responsible</th>
                <th className="t-header bc-java">Dependency</th>
              </tr>
            </thead>
            <tbody>
              {taskList.length > 0 ? (
                renderTaskRows(taskList)
              ) : (
                <tr>
                  <td colSpan="13" className="no-data">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default NewSummaryView;
